import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, FileCheck, ShieldCheck, ClipboardList, Users, Gavel, Banknote, Home, Landmark, AlertTriangle, Clock } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";
import FreePackage from "@/components/site/FreePackage";

const STEPS = [
  {
    icon: FileCheck,
    title: "Open the Probate Case",
    desc: "File a Petition for Probate with the Superior Court in the county where the decedent lived (e.g., Orange County Superior Court). Give legal notice to heirs and beneficiaries, and publish notice in a court-approved newspaper before your hearing date.",
  },
  {
    icon: ShieldCheck,
    title: "Get Appointed and Obtain Letters",
    desc: "Once the court confirms you, you'll receive Letters Testamentary (executor) or Letters of Administration (administrator) — the legal document that gives you authority to act on the estate's behalf, including signing for real estate transactions.",
  },
  {
    icon: ClipboardList,
    title: "Locate, Secure, and Inventory the Assets",
    desc: "Identify everything the decedent owned — bank accounts, investments, vehicles, personal property, and real property — and protect it from loss, damage, or waste while probate is pending. Keep insurance, mortgage, tax, and utilities current.",
  },
  {
    icon: Landmark,
    title: "Get a Court-Appointed Appraisal",
    desc: "A probate referee, appointed by the State Controller, formally appraises the estate's assets, including any homes or investment properties, to establish fair market value as of the date of death.",
  },
  {
    icon: Users,
    title: "Notify Creditors and Pay Valid Debts",
    desc: "Notify known creditors, give them the opportunity to file claims, then evaluate and pay legitimate debts and expenses from estate funds — often before any property can be sold or distributed.",
  },
  {
    icon: Banknote,
    title: "Manage Estate Funds and Records",
    desc: "All estate money goes into a separate, clearly labeled estate bank account — never mixed with your own. Keep detailed records of every transaction; beneficiaries have the legal right to request an accounting at any time.",
  },
  {
    icon: ClipboardList,
    title: "File Tax Returns",
    desc: "The estate's final personal income tax returns, and if applicable, a separate estate tax return, must be filed and any taxes owed paid from estate assets.",
  },
  {
    icon: Home,
    title: "Sell Real Property, If Needed",
    desc: "Real estate is frequently the largest asset in a California estate, and selling it correctly is one of the duties that carries the most risk if mishandled. Depending on whether you have full or limited authority under the IAEA, you may need court confirmation, a specific marketing/notice process, and an overbid hearing.",
  },
  {
    icon: Gavel,
    title: "Account to the Court and Distribute Assets",
    desc: "Once debts, taxes, and expenses are settled, file a Final Accounting and Petition for Final Distribution, notify all interested parties, and appear at a final hearing where the court approves how remaining assets — including sale proceeds — are distributed to beneficiaries.",
  },
  {
    icon: ShieldCheck,
    title: "Close the Estate",
    desc: "After distributions are made and approved, petition the court for final discharge, which formally ends your legal responsibility as personal representative.",
  },
];

const FIDUCIARY = [
  "Acting solely in the best interests of the estate and its beneficiaries — never your own",
  "Treating all beneficiaries fairly and equally",
  "Managing estate property with the same care a prudent person would use for their own assets",
  "Keeping estate funds and property completely separate from your personal finances",
];

const MISTAKES = [
  "Mixing estate funds with personal accounts",
  "Letting a vacant inherited property go uninsured or fall into disrepair",
  "Selling real estate without the required court notice, confirmation, or overbid process",
  "Missing creditor claim deadlines",
  "Failing to keep beneficiaries reasonably informed, which often triggers disputes or litigation",
];

const FAQS = [
  {
    q: "Do I have to hire a probate attorney as executor?",
    a: "California doesn't legally require it, but given the fiduciary liability involved, most personal representatives work with a probate attorney — and a probate-experienced real estate agent if the estate includes property.",
  },
  {
    q: "Can an executor sell estate property without all beneficiaries' approval?",
    a: "In many cases yes, particularly under full IAEA authority — but the sale still must follow specific notice requirements and may still require court confirmation depending on the authority granted and whether anyone objects.",
  },
  {
    q: "Is an executor personally liable for estate debts?",
    a: "Generally no — debts are paid from estate assets, not the executor's personal funds — but you can become personally liable if you mismanage the estate or distribute assets improperly before debts are settled.",
  },
  {
    q: "Are executor fees taxable?",
    a: "Yes. Unlike an inheritance, which beneficiaries receive tax-free, statutory executor or administrator fees are taxable income to the person who receives them.",
  },
  {
    q: "What happens if there's no will?",
    a: "The court appoints an administrator, typically the closest eligible relative, and the estate is distributed according to California's intestate succession laws rather than personal wishes.",
  },
];

export default function Probates() {
  return (
    <Layout>
      <PageHero
        breadcrumb="Home / Probates / Executor & Administrator Duties"
        overline="What California Law Requires"
        title="Responsibilities and Duties of Executors and"
        accentWord="Administrators in California."
        description="If you've been named executor in a loved one's will, or appointed administrator of an estate by an Orange County or Southern California probate court, you're stepping into a role with real legal weight. California law holds you to a strict standard of care — and many of your duties involve property. Here's a clear breakdown."
        image="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1600&q=80"
        rightImage="https://images.pexels.com/photos/8112172/pexels-photo-8112172.jpeg?auto=compress&cs=tinysrgb&w=1200"
        rightImageAlt="Executor reviewing estate documents with pen and paperwork"
      />

      {/* Executor vs Administrator */}
      <section className="py-16 bg-white" data-testid="exec-vs-admin">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-navy-100 bg-navy-50/40 p-8">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold-700 font-semibold">Named in the will</div>
              <h3 className="mt-2 font-heading text-2xl text-navy-900">Executor</h3>
              <p className="mt-3 text-sm text-navy-600 leading-relaxed">
                An <strong>executor</strong> is named in the decedent's will and confirmed by the probate court.
              </p>
            </div>
            <div className="rounded-3xl border border-navy-100 bg-navy-50/40 p-8">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold-700 font-semibold">Appointed by the court</div>
              <h3 className="mt-2 font-heading text-2xl text-navy-900">Administrator</h3>
              <p className="mt-3 text-sm text-navy-600 leading-relaxed">
                An <strong>administrator</strong> is appointed by the court when there's no will (intestate succession), or when the will doesn't name anyone able or willing to serve.
              </p>
            </div>
          </div>
          <div className="mt-6 rounded-2xl bg-gold-500/5 border border-gold-500/30 p-5 text-sm text-navy-700">
            Both roles are legally referred to as the <strong>personal representative</strong> of the estate, and in California, they carry nearly identical responsibilities. Whichever title applies to you — the duties below are the same.
          </div>
        </div>
      </section>

      {/* Core Fiduciary Duty */}
      <section className="py-20 bg-navy-50/40" data-testid="fiduciary-duty">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Scale size={36} className="text-gold-600" />
            <span className="section-overline mt-6 block">The Core Fiduciary Duty</span>
            <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
              The highest standard of <em className="italic text-gold-600">care</em> in California law.
            </h2>
            <p className="mt-4 text-navy-600">
              Breaching this duty — even unintentionally — can expose you to personal liability, removal by the court, or a surcharge ordered against you.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {FIDUCIARY.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`fiduciary-${i}`}
                className="rounded-2xl border border-navy-100 bg-white p-5 flex gap-4 items-start"
              >
                <div className="h-9 w-9 shrink-0 rounded-lg bg-navy-900 text-gold-400 flex items-center justify-center font-heading">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-sm text-navy-700 leading-relaxed pt-1">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 10-step duties */}
      <section className="py-20 bg-white" data-testid="steps-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="section-overline">Step-by-Step</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Ten duties of an executor or <em className="italic text-gold-600">administrator</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                data-testid={`step-${i}`}
                className="group rounded-2xl border border-navy-100 bg-navy-50/40 p-6 hover:bg-white hover:border-gold-400 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy-900 text-gold-400">
                    <s.icon size={18} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-navy-400 font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <h3 className="mt-5 font-heading text-lg text-navy-900 leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline callout */}
      <section className="py-16 bg-navy-900 relative overflow-hidden" data-testid="timeline-callout">
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-gold-500/15 blur-3xl" />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <Clock size={32} className="text-gold-400" />
            <span className="section-overline mt-4 block text-gold-400">How Long Does This Take?</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-white leading-tight">
              Typically <em className="italic text-gold-400">12 – 18 months</em>. Sometimes longer.
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              A straightforward California probate case generally takes 12 to 18 months from filing to final distribution. Estates that include a real estate sale, disputes among beneficiaries, or out-of-state assets often take longer — frequently 2 years or more.
            </p>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="font-heading text-3xl text-gold-400">12–18</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold mt-1">Months (typical)</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="font-heading text-3xl text-gold-400">2yr+</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold mt-1">Complex estates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-20 bg-white" data-testid="mistakes-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <AlertTriangle size={28} className="text-gold-600 shrink-0 mt-1" />
            <div>
              <span className="section-overline">Common Mistakes</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl text-navy-900 leading-tight">
                The five slip-ups that <em className="italic text-gold-600">cost families the most</em>.
              </h2>
            </div>
          </div>
          <div className="space-y-3">
            {MISTAKES.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`mistake-${i}`}
                className="rounded-xl border border-navy-100 bg-navy-50/40 p-5 flex items-start gap-3"
              >
                <span className="mt-1 h-2 w-2 rounded-full bg-gold-500 shrink-0" />
                <p className="text-sm text-navy-700">{m}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-navy-50/40" data-testid="probate-faqs">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <span className="section-overline">Frequently Asked</span>
          <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
            Executor and administrator <em className="italic text-gold-600">FAQs</em>.
          </h2>
          <div className="mt-10 divide-y divide-navy-200/60 border-y border-navy-200/60">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`probate-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-heading text-lg text-navy-900 pr-6">{f.q}</h3>
                  <span className="h-8 w-8 rounded-full border border-navy-200 flex items-center justify-center text-navy-700 group-open:bg-gold-500 group-open:text-navy-900 group-open:border-gold-500 transition-all">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-navy-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Selling inherited CTA */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-navy-500 leading-relaxed text-center max-w-3xl mx-auto">
            This page is for general informational purposes and does not constitute legal advice. Executors and administrators should consult a licensed California probate attorney regarding their specific legal obligations.
          </p>
        </div>
      </section>

      <FreePackage />
    </Layout>
  );
}
